// The monthly check-in: what a stored check-in is, and how WF78's chat reply looks to a model.
// Pure (no imports, no Node built-ins, ASCII): the back office imports it through logic/checkin.ts, and
// `node scripts/sync-checkin-reader.mjs --apply` splices its regions into WF78 and every model reader.
// EDIT HERE, then re-run the sync. S-163 compares every copy and pins the purity.

// CHECKIN-ROW-START
// The form's keys (sparks access-content{,-beta}/intakes/monthly-checkin.html). WF39 validates nothing,
// so every reader clamps the stored data before using it.
const CHECKIN_INTAKE_TYPES = ['monthly_checkin', 'weekly_checkin'];
const CHECKIN_AREAS = ['sleep', 'stress', 'activity', 'salt', 'food'];
const CHECKIN_CHIPS = ['headache', 'tiredness', 'dizziness', 'shortness_of_breath', 'chest_tightness', 'swelling', 'none'];
const CHECKIN_RED_CHIPS = ['chest_tightness', 'shortness_of_breath'];
const CHECKIN_AMBER_CHIPS = ['dizziness', 'swelling', 'headache'];
const CHECKIN_NOTE_MAX = 500;
function checkinScore(v) {
  const n = typeof v === 'number' ? v : (typeof v === 'string' && /^[1-5]$/.test(v.trim()) ? Number(v.trim()) : null);
  return Number.isInteger(n) && n >= 1 && n <= 5 ? n : null;
}
// A string, a JSON-string array, null or a list: only known chips survive, and 'none' only when alone.
function checkinChips(v) {
  let list = v;
  if (typeof list === 'string') {
    const s = list.trim();
    if (s.charAt(0) === '[') { try { list = JSON.parse(s); } catch (e) { list = []; } } else { list = s ? s.split(',') : []; }
  }
  if (!Array.isArray(list)) return [];
  const out = [];
  for (const c of list) {
    const k = String(c == null ? '' : c).trim().toLowerCase();
    if (CHECKIN_CHIPS.includes(k) && !out.includes(k)) out.push(k);
  }
  return out.length > 1 ? out.filter((c) => c !== 'none') : out;
}
function checkinData(raw) {
  let d = raw;
  if (typeof d === 'string') { try { d = JSON.parse(d); } catch (e) { d = null; } }
  if (!d || typeof d !== 'object' || Array.isArray(d)) d = {};
  const scores = {};
  for (const a of CHECKIN_AREAS) scores[a] = checkinScore(d[a + '_1_5']);
  scores.device = checkinScore(d.device_issue_1_5);
  const note = typeof d.note === 'string' ? d.note.trim().slice(0, CHECKIN_NOTE_MAX) : '';
  return { scores, chips: checkinChips(d.symptoms), note };
}
// Chips only: a red raised by the note is decided server-side (WF78) and shows on the task.
function hasCheckinRedFlag(raw) {
  return checkinData(raw).chips.some((c) => CHECKIN_RED_CHIPS.includes(c));
}
// The answers as one comparable string, keys sorted; WF39's own back-link key is left out. WF78 finds the
// row WF39 stored with it, because n8n drops WF39's result when its run ends on a second output.
function checkinSig(raw) {
  let d = raw;
  if (typeof d === 'string') { try { d = JSON.parse(d); } catch (e) { return ''; } }
  const canon = (v) => (Array.isArray(v) ? '[' + v.map(canon).join(',') + ']'
    : (v && typeof v === 'object') ? '{' + Object.keys(v).filter((k) => k !== 'source_task_id').sort()
      .map((k) => JSON.stringify(k) + ':' + canon(v[k])).join(',') + '}'
    : JSON.stringify(v === undefined ? null : v));
  return d && typeof d === 'object' && !Array.isArray(d) ? canon(d) : '';
}
// CHECKIN-ROW-END

// CHECKIN-REPLY-LABEL-START
// Every reader that hands an outreach body to a model sees WF78's check-in reply as this label, never its
// body ("call 911", a symptom, the first name). Keyed on msg_type alone, because WF34's Parse & Validate
// keeps no direction or source; never on a patient's own row, so a forged msg_type cannot hide their words.
const CHECKIN_REPLY_MSG_TYPE = 'checkin_reply';
const CHECKIN_REPLY_LABEL = '[check-in reply sent]';
function checkinReplyLabelOf(row) {
  if (!row || row.direction === 'inbound' || row.source === 'patient' || row.sender === 'patient') return null;
  return row.msg_type === CHECKIN_REPLY_MSG_TYPE ? CHECKIN_REPLY_LABEL : null;
}
// "Answers nothing": a check-in reply never closes a patient's open question (WF2 lane 4, Suggest).
// Needs the source too, so a coordinator's row carrying that msg_type still answers.
function isCheckinReply(row) {
  return !!row && row.direction === 'outbound' && row.source === 'ai_agent' && row.msg_type === CHECKIN_REPLY_MSG_TYPE;
}
function labelCheckinRow(row) {
  const label = checkinReplyLabelOf(row);
  return label ? Object.assign({}, row, { body: label }) : row;
}
// CHECKIN-REPLY-LABEL-END

export {
  CHECKIN_INTAKE_TYPES, CHECKIN_AREAS, CHECKIN_CHIPS, CHECKIN_RED_CHIPS, CHECKIN_AMBER_CHIPS, CHECKIN_NOTE_MAX,
  checkinScore, checkinChips, checkinData, hasCheckinRedFlag, checkinSig,
  CHECKIN_REPLY_MSG_TYPE, CHECKIN_REPLY_LABEL, checkinReplyLabelOf, isCheckinReply, labelCheckinRow,
};
